// Market hours helpers for SPX / 0-DTE flow dashboard.
//
// The dashboard presents the 9:30-16:00 ET cash session. SPX options trade for
// another 15 minutes, but those prints are outside the dashboard session.

import { earlyCloseMinute, isHoliday } from './marketCalendar';

const ET_PARTS_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: 'numeric',
  minute: 'numeric',
  hour12: false,
});

const ET_DATE_KEY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function etParts(epochMs) {
  return ET_PARTS_FORMATTER.formatToParts(new Date(epochMs));
}

const MARKET_OPEN_MINUTE = 570;
const CHAIN_SNAPSHOT_CLOSE_MINUTE = 960;

export function isMarketSessionDate(dateKey) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey || '')) return false;
  const date = new Date(`${dateKey}T00:00:00Z`);
  return !Number.isNaN(date.getTime())
    && date.toISOString().slice(0, 10) === dateKey
    && date.getUTCDay() !== 0
    && date.getUTCDay() !== 6
    && !isHoliday(dateKey);
}

function newYorkMinuteEpoch(dateKey, minuteOfDay) {
  const [year, month, day] = dateKey.split('-').map(Number);
  const utcOpen = Date.UTC(
    year,
    month - 1,
    day,
    Math.floor(minuteOfDay / 60),
    minuteOfDay % 60
  );
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(utcOpen));
  const value = (type) => Number(parts.find((part) => part.type === type).value);
  const localAsUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour'),
    value('minute'),
    value('second')
  );

  return utcOpen - (localAsUtc - utcOpen);
}

/**
 * Epoch ms of the 16:00 ET cash close (or the early close on a half day).
 *
 * Charts anchor their right edge here. Index options keep printing until 16:15
 * and futures-derived bars can run later still, so without this the visible
 * range drifts past the close and shows a trailing sliver nobody trades.
 */
export function sessionCloseEpochMs(epochMs = Date.now()) {
  const dateKey = etDateKey(epochMs);
  return newYorkMinuteEpoch(dateKey, earlyCloseMinute(dateKey) ?? CHAIN_SNAPSHOT_CLOSE_MINUTE);
}

export function sessionCloseEpochSeconds(epochMs = Date.now()) {
  return Math.floor(sessionCloseEpochMs(epochMs) / 1000);
}

export function sessionOpenEpochMs(epochMs = Date.now()) {
  return newYorkMinuteEpoch(etDateKey(epochMs), MARKET_OPEN_MINUTE);
}

export function sessionOpenEpochSeconds(epochMs = Date.now()) {
  return Math.floor(sessionOpenEpochMs(epochMs) / 1000);
}

function nextTradingDate(dateKey, includeToday) {
  const [year, month, day] = dateKey.split('-').map(Number);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  if (!includeToday) candidate.setUTCDate(candidate.getUTCDate() + 1);

  while (!isMarketSessionDate(candidate.toISOString().slice(0, 10))) {
    candidate.setUTCDate(candidate.getUTCDate() + 1);
  }

  return candidate.toISOString().slice(0, 10);
}

function previousTradingDate(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  candidate.setUTCDate(candidate.getUTCDate() - 1);

  while (!isMarketSessionDate(candidate.toISOString().slice(0, 10))) {
    candidate.setUTCDate(candidate.getUTCDate() - 1);
  }

  return candidate.toISOString().slice(0, 10);
}

export function mostRecentSessionDate(epochMs = Date.now()) {
  const parts = etParts(epochMs);
  const hour = parseInt(parts.find((part) => part.type === 'hour').value, 10) % 24;
  const minute = parseInt(parts.find((part) => part.type === 'minute').value, 10);
  const dateKey = etDateKey(epochMs);
  const sessionStarted = hour * 60 + minute >= MARKET_OPEN_MINUTE;

  if (isMarketSessionDate(dateKey) && sessionStarted) {
    return dateKey;
  }

  return previousTradingDate(dateKey);
}

export function isDuringMarketHours(epochMs) {
  const parts = etParts(epochMs);
  const date = etDateKey(epochMs);
  if (!isMarketSessionDate(date)) return false;

  const hour = parseInt(parts.find((part) => part.type === 'hour').value, 10);
  const minute = parseInt(parts.find((part) => part.type === 'minute').value, 10);
  const minuteOfDay = hour * 60 + minute;
  const closeMinute = earlyCloseMinute(date) ?? CHAIN_SNAPSHOT_CLOSE_MINUTE;

  return minuteOfDay >= MARKET_OPEN_MINUTE && minuteOfDay <= closeMinute;
}

export function isMarketOpen() {
  return isDuringMarketHours(Date.now());
}

export function millisecondsUntilNextMarketOpen(epochMs = Date.now()) {
  const parts = etParts(epochMs);
  const hour = parseInt(parts.find((part) => part.type === 'hour').value, 10);
  const minute = parseInt(parts.find((part) => part.type === 'minute').value, 10);
  const dateKey = etDateKey(epochMs);
  const nextDate = nextTradingDate(
    dateKey,
    isMarketSessionDate(dateKey)
      && hour * 60 + minute < MARKET_OPEN_MINUTE
  );

  return Math.max(0, newYorkMinuteEpoch(nextDate, MARKET_OPEN_MINUTE) - epochMs);
}

export function sessionAnchoredValue(liveValue, sessionValue, epochMs = Date.now()) {
  if (isDuringMarketHours(epochMs)) {
    return Number.isFinite(liveValue) ? liveValue : sessionValue ?? null;
  }
  return Number.isFinite(sessionValue) ? sessionValue : (liveValue ?? null);
}

export function sessionAnchoredSpot(liveSpot, snaps, epochMs = Date.now()) {
  return sessionAnchoredValue(liveSpot, snaps?.[snaps.length - 1]?.spot, epochMs);
}

export function etDateKey(epochMs) {
  return ET_DATE_KEY_FORMATTER.format(new Date(epochMs));
}

export function latestTradeDayOnly(items, getT = (item) => item.t) {
  if (!items?.length) return items ?? [];

  const lastKey = etDateKey(getT(items[items.length - 1]));
  let start = items.length - 1;
  while (start > 0 && etDateKey(getT(items[start - 1])) === lastKey) {
    start -= 1;
  }
  return start === 0 ? items : items.slice(start);
}
